import React from 'react';
import { 
  Container, 
  Grid, 
  Typography, 
  Box, 
  Card, 
  CardContent,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Switch,
  FormControlLabel,
  useTheme,
  alpha,
  Avatar
} from '@mui/material';
import { 
  Person, 
  CreditCard, 
  Notifications, 
  Security, 
  Business,
  Star,
  CheckCircle,
  Settings as SettingsIcon,
  Upgrade,
  Receipt,
  Support
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import PremiumCard from '../components/ui/PremiumCard';
import PageHeader from '../components/ui/PageHeader';
import { DESIGN_SYSTEM } from '../theme/designSystem';

/**
 * Página de Configuración Completa
 * Incluye gestión de cuenta, planes, facturación y configuraciones
 */
export default function SettingsNew() {
  const theme = useTheme();
  const { userData } = useAuth();

  const currentPlan = {
    name: 'Premium',
    price: '$29',
    period: '/mes',
    features: [
      'Análisis avanzado de productos',
      'Segmentación de clientes',
      'Reportes exportables',
      'Soporte prioritario',
      'Datos en tiempo real'
    ]
  };

  const availablePlans = [
    {
      name: 'Básico',
      price: '$9',
      period: '/mes',
      description: 'Para tiendas pequeñas',
      features: ['Dashboard básico', 'Estadísticas simples', 'Soporte email'],
      current: false
    },
    {
      name: 'Premium',
      price: '$29',
      period: '/mes',
      description: 'Para tiendas en crecimiento',
      features: ['Todo lo básico', 'Análisis avanzado', 'Segmentación', 'Reportes PDF'],
      current: true,
      popular: true
    },
    {
      name: 'Enterprise',
      price: '$99',
      period: '/mes',
      description: 'Para grandes tiendas',
      features: ['Todo lo premium', 'IA predictiva', 'API personalizada', 'Soporte 24/7'],
      current: false
    }
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Unificado */}
      <PageHeader 
        title={DESIGN_SYSTEM.pageHeaders.settings.title}
        subtitle={DESIGN_SYSTEM.pageHeaders.settings.subtitle}
        icon={<SettingsIcon />}
      />

      <Grid container spacing={3}>
        {/* Información de Usuario */}
        <Grid item xs={12} md={4}>
          <PremiumCard
            title="Información de Usuario"
            icon={<Person />}
            badge="Cuenta"
          >
            <Box textAlign="center" py={2}>
              <Avatar 
                sx={{ 
                  width: 80, 
                  height: 80, 
                  mx: 'auto', 
                  mb: 2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  fontSize: '2rem',
                  fontWeight: 700
                }}
              >
                {userData?.name?.charAt(0) || 'U'}
              </Avatar>
              
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {userData?.name || 'Usuario'}
              </Typography>
              
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {userData?.email || 'email@ejemplo.com'}
              </Typography>
              
              <Chip 
                label={`Store ID: ${userData?.tiendaId || 'N/A'}`}
                size="small"
                sx={{ mt: 1 }}
              />
              
              <Box mt={2}>
                <Button variant="outlined" size="small" fullWidth>
                  Editar Perfil
                </Button>
              </Box>
            </Box>
          </PremiumCard>
        </Grid>

        {/* Plan Actual */}
        <Grid item xs={12} md={8}>
          <PremiumCard
            title="Plan Actual"
            icon={<Star />}
            badge="Premium"
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
              <Box>
                <Typography variant="h4" fontWeight={700} color="primary.main">
                  {currentPlan.name}
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  {currentPlan.price}{currentPlan.period}
                </Typography>
              </Box>
              
              <Box textAlign="right">
                <Chip 
                  label="Activo" 
                  color="success" 
                  icon={<CheckCircle />}
                  sx={{ mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  Próxima facturación: 15 Ago 2025
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" fontWeight={600} gutterBottom>
              Características incluidas:
            </Typography>
            
            <List dense>
              {currentPlan.features.map((feature, index) => (
                <ListItem key={index} sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircle color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={feature} />
                </ListItem>
              ))}
            </List>

            <Box mt={3} display="flex" gap={2}>
              <Button variant="contained" startIcon={<Upgrade />}>
                Cambiar Plan
              </Button>
              <Button variant="outlined" startIcon={<Receipt />}>
                Ver Facturas
              </Button>
            </Box>
          </PremiumCard>
        </Grid>

        {/* Planes Disponibles */}
        <Grid item xs={12}>
          <PremiumCard
            title="Planes Disponibles"
            subtitle="Elige el plan que mejor se adapte a tu tienda"
            icon={<CreditCard />}
          >
            <Grid container spacing={3}>
              {availablePlans.map((plan, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Card 
                    sx={{ 
                      position: 'relative',
                      border: plan.current ? `2px solid ${theme.palette.primary.main}` : '1px solid rgba(0,0,0,0.1)',
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`,
                        transform: 'translateY(-4px)'
                      }
                    }}
                  >
                    {plan.popular && (
                      <Chip 
                        label="Más Popular" 
                        color="primary" 
                        size="small"
                        sx={{ 
                          position: 'absolute', 
                          top: -10, 
                          left: '50%', 
                          transform: 'translateX(-50%)',
                          zIndex: 1
                        }}
                      />
                    )}
                    
                    <CardContent sx={{ textAlign: 'center', p: 3 }}>
                      <Typography variant="h5" fontWeight={700} gutterBottom>
                        {plan.name}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {plan.description}
                      </Typography>
                      
                      <Box my={2}>
                        <Typography variant="h3" fontWeight={700} color="primary.main">
                          {plan.price}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {plan.period}
                        </Typography>
                      </Box>

                      <List dense>
                        {plan.features.map((feature, idx) => (
                          <ListItem key={idx} sx={{ px: 0, justifyContent: 'center' }}>
                            <ListItemText 
                              primary={feature} 
                              sx={{ textAlign: 'center' }}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        ))}
                      </List>

                      <Box mt={3}>
                        {plan.current ? (
                          <Button variant="contained" disabled fullWidth>
                            Plan Actual
                          </Button>
                        ) : (
                          <Button variant="outlined" fullWidth>
                            Seleccionar Plan
                          </Button>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </PremiumCard>
        </Grid>

        {/* Configuraciones */}
        <Grid item xs={12} md={6}>
          <PremiumCard
            title="Configuraciones"
            icon={<SettingsIcon />}
          >
            <List>
              <ListItem>
                <ListItemIcon>
                  <Notifications />
                </ListItemIcon>
                <ListItemText 
                  primary="Notificaciones por email"
                  secondary="Recibir alertas y reportes"
                />
                <FormControlLabel
                  control={<Switch defaultChecked />}
                  label=""
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <Security />
                </ListItemIcon>
                <ListItemText 
                  primary="Autenticación de dos factores"
                  secondary="Seguridad adicional para tu cuenta"
                />
                <FormControlLabel
                  control={<Switch />}
                  label=""
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <Business />
                </ListItemIcon>
                <ListItemText 
                  primary="Sincronización automática"
                  secondary="Actualizar datos cada 15 minutos"
                />
                <FormControlLabel
                  control={<Switch defaultChecked />}
                  label=""
                />
              </ListItem>
            </List>
          </PremiumCard>
        </Grid>

        {/* Soporte */}
        <Grid item xs={12} md={6}>
          <PremiumCard
            title="Soporte y Ayuda"
            icon={<Support />}
          >
            <Box py={2}>
              <Typography variant="body1" gutterBottom>
                ¿Necesitas ayuda con Nubelytics?
              </Typography>
              
              <Typography variant="body2" color="text.secondary" paragraph>
                Nuestro equipo está aquí para ayudarte a sacar el máximo provecho de tu análisis de datos.
              </Typography>

              <Box display="flex" flexDirection="column" gap={2}>
                <Button variant="contained" startIcon={<Support />} fullWidth>
                  Contactar Soporte
                </Button>
                
                <Button variant="outlined" fullWidth>
                  Centro de Ayuda
                </Button>
                
                <Button variant="outlined" fullWidth>
                  Documentación
                </Button>
              </Box>

              <Box mt={3} p={2} sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), borderRadius: 2 }}>
                <Typography variant="body2" color="info.main" fontWeight={600}>
                  💡 Tip: Como usuario Premium, tienes acceso a soporte prioritario
                </Typography>
              </Box>
            </Box>
          </PremiumCard>
        </Grid>
      </Grid>
    </Container>
  );
}
